/**
 * House of Dusk · footer global (lot 2)
 *
 * <hod-cookie-prefs> : lien « Cookie preferences » de la ligne legale (Q41).
 * Il rouvre les preferences de la banniere de consentement native de Shopify,
 * par l'API documentee window.privacyBanner.showPreferences().
 *
 * Tant que la banniere n'est pas activee dans l'admin (Parametres, Confidentialite
 * des clients), l'API n'existe pas : l'element de liste reste masque, jamais de
 * bouton mort. Le script de la banniere arrive apres le chargement de la page,
 * d'ou une verification bornee (20 essais, 10 s). Dans l'editeur de theme, le
 * Liquid rend l'element visible pour qu'il reste selectionnable.
 *
 * Message d'inscription (succes ou erreur) rendu au chargement qui suit l'envoi : il recoit le
 * focus ([data-hod-focus-on-load]) pour qu'un lecteur d'ecran l'annonce ; une region live deja
 * presente au chargement ne l'est pas de facon fiable. L'ancre de Shopify gere le defilement.
 *
 * Module sans dependance, charge par la section seulement si le bloc cookies existe ou si
 * un message d'inscription est affiche.
 */

const POLL_MS = 500;
const POLL_MAX = 20;

const bannerApi = () => {
  const api = window.privacyBanner;
  return api && typeof api.showPreferences === 'function' ? api : null;
};

class HodCookiePrefs extends HTMLElement {
  #timer = 0;
  #tries = 0;
  #button = null;

  #onClick = (event) => {
    const api = bannerApi();
    if (!api) return;
    event.preventDefault();
    api.showPreferences();
  };

  #check = () => {
    if (bannerApi()) {
      const item = this.closest('li');
      if (item) item.hidden = false;
      return;
    }
    if (this.#tries < POLL_MAX) {
      this.#tries += 1;
      this.#timer = window.setTimeout(this.#check, POLL_MS);
    }
  };

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;
    this.#button.addEventListener('click', this.#onClick);
    this.#tries = 0;
    this.#check();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    if (this.#button) this.#button.removeEventListener('click', this.#onClick);
  }
}

if (!customElements.get('hod-cookie-prefs')) {
  customElements.define('hod-cookie-prefs', HodCookiePrefs);
}

const newsletterMessage = document.querySelector('[data-hod-focus-on-load]');
if (newsletterMessage instanceof HTMLElement) newsletterMessage.focus({ preventScroll: true });


/**
 * Bande logo du bas de page (spec 31_SPEC_FOOTER_LOGO.md, dessin dans hod-footer.css §7).
 *
 * Contrat du theme (hod-base.css §10.6) : la CSS seule rend l'ETAT FINAL. Sans JS, avec un
 * JS en erreur, sans IntersectionObserver ou sous prefers-reduced-motion, la bande est deja
 * le logo pose (demi-disque sur l'horizon). Ce script ne fait qu'AJOUTER un depart :
 *   .hod-flogo--armed  filet ferme au centre, disque entier au-dessus de la ligne
 *   .hod-flogo--in     lecture, une seule fois, puis l'observation est relachee
 *
 * Une bande deja a l'ecran au chargement n'est jamais armee : elle reste en etat final,
 * aucun retour en arriere visible. Le Liquid ne pose data-hod-flogo que si le reglage
 * d'animation de la section est allume.
 */
const FLOGO_VIEW_RATIO = 0.9;

/**
 * @param {ParentNode} root
 * @param {IntersectionObserver} observer
 */
function flogoArm(root, observer) {
  const bands = Array.from(root.querySelectorAll('[data-hod-flogo]:not([data-hod-flogo-set])'));
  if (root instanceof Element && root.matches('[data-hod-flogo]:not([data-hod-flogo-set])')) bands.unshift(root);
  for (const band of bands) {
    band.setAttribute('data-hod-flogo-set', '');
    if (band.getBoundingClientRect().top < window.innerHeight * FLOGO_VIEW_RATIO) continue;
    band.classList.add('hod-flogo--armed');
    observer.observe(band);
  }
}

const hodFlogoWindow = /** @type {any} */ (window);
if (!hodFlogoWindow.__hodFlogoBound && 'IntersectionObserver' in window) {
  hodFlogoWindow.__hodFlogoBound = true;
  const flogoStill = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!flogoStill.matches) {
    const flogoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Une bande deja depassee (ancre, retour arriere, position restauree) n'entrera
          // jamais : elle est jouee sans attendre. Rien ne peut rester a l'etat de depart.
          if (!entry.isIntersecting && entry.boundingClientRect.top > 0) continue;
          entry.target.classList.add('hod-flogo--in');
          flogoObserver.unobserve(entry.target);
        }
      },
      // Aucune marge negative en bas : la bande est COLLEE au bas du document (v4 : ~240 px
      // a 375, ~512 px a 1440). Une marge negative rognerait la seule portion de fenetre ou elle
      // apparait, et l'animation ne partirait jamais. Seuil a 0,25 : une bande entierement
      // visible le depasse toujours, et en cours de defilement elle part des son quart.
      { threshold: 0.25 }
    );
    flogoArm(document, flogoObserver);
    // Editeur de theme : le footer recharge est rearme, jamais deux fois.
    document.addEventListener('shopify:section:load', (event) => {
      const target = /** @type {any} */ (event).target;
      if (target instanceof Element) flogoArm(target, flogoObserver);
    });
    // Le visiteur coupe les animations en cours de route : tout se pose.
    flogoStill.addEventListener('change', (event) => {
      if (!event.matches) return;
      for (const band of document.querySelectorAll('.hod-flogo--armed')) {
        band.classList.add('hod-flogo--in');
        flogoObserver.unobserve(band);
      }
    });
  }
}
